import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Drawer, DrawerTabBar, Select, ToolbarSearch } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import {
  useAllFunnels,
  useCreateFunnel,
  useUpdateFunnel,
  useDeleteFunnel,
  useValidateDeleteFunnelLazy,
  useStepsByFunnelId,
} from '../hooks';
import { FunnelDeleteModal } from './FunnelDeleteModal';
import type {
  FunnelResponse,
  DistributionConfig,
  FunnelCaptureConfig,
  FunnelLeadsDisposition,
} from '@troia-v3/types';
import { AssignmentConfigSection } from '@/shared/components/assignment';
import { ContextUsersTab } from '@/shared/components/context-users';
import { RoutingRulesTab } from './RoutingRulesTab';
import { StepsManager } from './StepsManager';
import { FunnelAlertsTab } from './FunnelAlertsTab';
import { FunnelCaptureTab } from './FunnelCaptureTab';
import { Icon } from '@/shared/icons';

/**
 * Drawer lateral Master-Detail para configuração de Funis do CRM.
 *
 * Substitui a `FunnelsSettingsModal` (modal central com view empilhada
 * list→edit→manage-steps) por um layout Master-Detail: lista de funis
 * sempre visível à esquerda + detalhe com abas à direita. "Etapas" deixa
 * de ser uma view separada e vira apenas mais uma aba.
 *
 * 100% de reuso de hooks/mutations e sub-componentes já existentes
 * (`useFunnels`, `useCreateFunnel`, `useUpdateFunnel`, `useStepsByFunnelId`,
 * `AssignmentConfigSection`, `ContextUsersTab`, `RoutingRulesTab`,
 * `StepsManager`) — só a casca (modal→drawer) e a navegação mudaram.
 *
 * Ver plano: DOCS/superpowers/plans/2026-07-17-funnels-drawer-master-detail.md
 *
 * @example
 * <FunnelsSettingsDrawer
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 * />
 */
export interface FunnelsSettingsDrawerProps {
  /** Drawer open state */
  isOpen: boolean;
  /** Callback when the drawer should close */
  onClose: () => void;
  /** Initial view to show when the drawer opens */
  initialView?: 'list' | 'create-funnel';
  /**
   * Deep-link: abre o drawer com este funil já selecionado (ignorado quando
   * `initialView === 'create-funnel'`). A seleção acontece quando a lista de
   * funis termina de carregar; id inexistente cai na lista normal.
   */
  initialFunnelId?: string | null;
  /** Deep-link: aba ativa ao selecionar o funil de `initialFunnelId`. */
  initialTab?: FunnelDrawerTab;
}

export type FunnelDrawerTab = 'basic' | 'steps' | 'alerts' | 'types' | 'distribution' | 'users' | 'routing' | 'capture';

interface FunnelFormState {
  name: string;
  description: string;
  color: string;
  assignmentConfig?: DistributionConfig;
  allowDuplicateContacts: boolean;
  duplicateCheckScope: 'funnel' | 'all_funnels';
  types: string[];
  /** `null` = captura desligada (PUT manda `capture: null`); objeto = ligada. */
  capture: FunnelCaptureConfig | null;
}

const EMPTY_FORM: FunnelFormState = {
  name: '',
  description: '',
  color: '#8b5cf6',
  assignmentConfig: undefined,
  allowDuplicateContacts: true,
  duplicateCheckScope: 'funnel',
  types: [],
  capture: null,
};

const TAB_LABELS: Record<FunnelDrawerTab, string> = {
  basic: 'Básico',
  steps: 'Etapas',
  alerts: 'Alertas',
  types: 'Tipos',
  distribution: 'Distribuição',
  users: 'Usuários',
  routing: 'Roteamento',
  capture: 'Captura',
};

export const FunnelsSettingsDrawer: React.FC<FunnelsSettingsDrawerProps> = ({
  isOpen,
  onClose,
  initialView = 'list',
  initialFunnelId = null,
  initialTab,
}) => {
  const confirm = useConfirm();
  const [selectedFunnelId, setSelectedFunnelId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<FunnelDrawerTab>('basic');
  const [funnelForm, setFunnelForm] = useState<FunnelFormState>(EMPTY_FORM);
  const [newTypeName, setNewTypeName] = useState('');
  const [pendingInitialFunnelId, setPendingInitialFunnelId] = useState<string | null>(null);
  /** Funil aguardando decisão sobre o destino dos leads (RD2) — `null` = modal fechado. */
  const [deleteTarget, setDeleteTarget] = useState<{ funnel: FunnelResponse; leadCount: number } | null>(null);

  // RD5: a lista master busca no cliente, então precisa de TODOS os funis. Com
  // `useFunnels({ limit: 100 })` o tenant de 128 funis escondia 28 sem aviso e
  // a busca não os encontrava. `useAllFunnels` percorre as páginas da rota.
  const { data: funnelsData, isLoading, isFetching } = useAllFunnels();
  const createFunnel = useCreateFunnel();
  const updateFunnel = useUpdateFunnel();
  const deleteFunnel = useDeleteFunnel();
  const validateDeleteFunnel = useValidateDeleteFunnelLazy();

  const funnels = useMemo(() => funnelsData ?? [], [funnelsData]);

  const filteredFunnels = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return funnels;
    return funnels.filter((f) => f.name.toLowerCase().includes(q));
  }, [funnels, search]);

  const selectedFunnel = useMemo(
    () => funnels.find((f) => f.id === selectedFunnelId) ?? null,
    [funnels, selectedFunnelId],
  );

  // Reset state whenever the drawer opens, honoring `initialView` — mirrors
  // the modal's `useEffect` that reset `view`/`activeTab` on open.
  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setActiveTab('basic');
    if (initialView === 'create-funnel') {
      setIsCreating(true);
      setSelectedFunnelId(null);
      setFunnelForm(EMPTY_FORM);
      setPendingInitialFunnelId(null);
    } else {
      setIsCreating(false);
      setSelectedFunnelId(null);
      setPendingInitialFunnelId(initialFunnelId);
    }
  }, [isOpen, initialView, initialFunnelId]);

  // Deep-link: quando a lista carrega, seleciona o funil de `initialFunnelId`
  // (popula o form como um clique na lista) e cai na aba pedida.
  useEffect(() => {
    if (!pendingInitialFunnelId) return;
    // Usuário já interagiu (rascunho de novo funil ou seleção manual) —
    // o deep-link perde a vez em vez de atropelar o estado dele.
    if (isCreating || selectedFunnelId) {
      setPendingInitialFunnelId(null);
      return;
    }
    const funnel = funnels.find((f) => f.id === pendingInitialFunnelId);
    if (funnel) {
      handleSelectFunnel(funnel);
      if (initialTab) setActiveTab(initialTab);
      setPendingInitialFunnelId(null);
    } else if (!isLoading && !isFetching) {
      // `isFetching` cobre cache stale sendo revalidado em background — só
      // desiste com a lista fresca sem o funil (excluído/fora do escopo/página).
      setPendingInitialFunnelId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSelectFunnel é estável por closure sobre setters
  }, [pendingInitialFunnelId, funnels, isLoading, isFetching, initialTab, isCreating, selectedFunnelId]);

  const handleSelectFunnel = (funnel: FunnelResponse) => {
    setIsCreating(false);
    setSelectedFunnelId(funnel.id);
    setFunnelForm({
      name: funnel.name,
      description: funnel.description || '',
      color: funnel.color,
      assignmentConfig: funnel.assignmentConfig,
      allowDuplicateContacts: funnel.allowDuplicateContacts ?? true,
      duplicateCheckScope: funnel.duplicateCheckScope ?? 'funnel',
      types: funnel.types || [],
      capture: funnel.capture ?? null,
    });
    setActiveTab('basic');
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setSelectedFunnelId(null);
    setFunnelForm(EMPTY_FORM);
    setActiveTab('basic');
  };

  const handleCreateFunnel = async () => {
    try {
      const created = await createFunnel.mutateAsync({
        name: funnelForm.name,
        description: funnelForm.description,
        color: funnelForm.color,
        order: funnels.length,
        assignmentConfig: funnelForm.assignmentConfig,
        allowDuplicateContacts: funnelForm.allowDuplicateContacts,
        duplicateCheckScope: funnelForm.duplicateCheckScope,
        types: funnelForm.types.length > 0 ? funnelForm.types : undefined,
        ...(funnelForm.capture !== null ? { capture: funnelForm.capture } : {}),
      });
      setIsCreating(false);
      handleSelectFunnel(created);
    } catch (error) {
      console.error('Error creating funnel:', error);
    }
  };

  const handleEditFunnel = async () => {
    if (!selectedFunnel) return;

    try {
      await updateFunnel.mutateAsync({
        id: selectedFunnel.id,
        data: {
          name: funnelForm.name,
          description: funnelForm.description,
          color: funnelForm.color,
          assignmentConfig: funnelForm.assignmentConfig,
          allowDuplicateContacts: funnelForm.allowDuplicateContacts,
          duplicateCheckScope: funnelForm.duplicateCheckScope,
          types: funnelForm.types.length > 0 ? funnelForm.types : undefined,
          // `null` desliga (backend faz $unset); objeto salva; nunca omitir — o
          // usuário pode ter desligado nesta sessão de edição.
          capture: funnelForm.capture,
        },
      });
    } catch (error) {
      console.error('Error updating funnel:', error);
    }
  };

  /**
   * Clique em "Excluir funil" (RD2).
   *
   * Pergunta ao backend quantos leads o funil tem (`validate-delete`) e
   * bifurca: funil vazio segue na confirmação simples de sempre; funil COM
   * leads abre o `FunnelDeleteModal`, onde o usuário escolhe entre excluir os
   * leads junto ou transferi-los para outro funil/etapa.
   */
  const handleDeleteFunnel = async (funnel: FunnelResponse) => {
    let leadCount = 0;
    try {
      const validation = await validateDeleteFunnel(funnel.id);
      leadCount = validation.leadCount ?? 0;
    } catch (error) {
      // Falha na consulta não pode travar a exclusão de um funil vazio: segue
      // pela confirmação simples e, se houver lead, quem recusa é o backend
      // (400 com a contagem na mensagem).
      console.error('Error validating funnel deletion:', error);
    }

    if (leadCount > 0) {
      setDeleteTarget({ funnel, leadCount });
      return;
    }

    // NUNCA window.confirm — modal de confirmação via ConfirmProvider (promise-based)
    const confirmed = await confirm({
      title: 'Excluir funil',
      message: 'Deseja realmente excluir este funil? Esta ação não pode ser desfeita.',
      confirmButtonText: 'Excluir',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }

    try {
      await deleteFunnel.mutateAsync({ id: funnel.id });
      if (selectedFunnelId === funnel.id) {
        setSelectedFunnelId(null);
      }
    } catch (error) {
      console.error('Error deleting funnel:', error);
    }
  };

  /** Confirmação vinda do `FunnelDeleteModal`, já com o destino dos leads. */
  const handleConfirmDeleteWithLeads = async (leads: FunnelLeadsDisposition) => {
    if (!deleteTarget) return;
    const { funnel } = deleteTarget;
    try {
      await deleteFunnel.mutateAsync({ id: funnel.id, body: { leads } });
      setDeleteTarget(null);
      if (selectedFunnelId === funnel.id) {
        setSelectedFunnelId(null);
      }
    } catch (error) {
      // Modal continua aberto: o toast de erro explica o motivo (ex.: etapa que
      // não pertence ao funil de destino) e o usuário corrige a escolha.
      console.error('Error deleting funnel:', error);
    }
  };

  const handleClose = () => {
    setSelectedFunnelId(null);
    setIsCreating(false);
    setActiveTab('basic');
    setSearch('');
    setFunnelForm(EMPTY_FORM);
    onClose();
  };

  // Mobile (colunas empilhadas): sai do detalhe de volta pra lista.
  const handleBackToList = () => {
    setSelectedFunnelId(null);
    setIsCreating(false);
    setActiveTab('basic');
    setFunnelForm(EMPTY_FORM);
  };

  const handleCancel = () => {
    if (isCreating) {
      setIsCreating(false);
      setFunnelForm(EMPTY_FORM);
      setActiveTab('basic');
    } else {
      handleClose();
    }
  };

  const handleAddType = () => {
    const trimmed = newTypeName.trim();
    if (trimmed && !funnelForm.types.includes(trimmed)) {
      setFunnelForm({ ...funnelForm, types: [...funnelForm.types, trimmed] });
      setNewTypeName('');
    }
  };

  const showDetail = isCreating || !!selectedFunnel;

  const tabs: FunnelDrawerTab[] = isCreating || !selectedFunnel
    ? ['basic', 'steps', 'types', 'distribution', 'capture']
    : ['basic', 'steps', 'alerts', 'types', 'distribution', 'users', 'routing', 'capture'];

  const subtitle = isCreating
    ? 'Criando novo funil'
    : selectedFunnel
      ? `Editando ${selectedFunnel.name}`
      : 'Selecione um funil à esquerda';

  const footer = showDetail && activeTab !== 'steps' && activeTab !== 'alerts' ? (
    <>
      <Button variant="secondary" onClick={handleCancel} data-testid="funnels-drawer-cancel">
        Cancelar
      </Button>
      <Button
        variant="primary"
        onClick={isCreating ? handleCreateFunnel : handleEditFunnel}
        disabled={!funnelForm.name.trim()}
        loading={isCreating ? createFunnel.isPending : updateFunnel.isPending}
        data-testid="funnels-drawer-save"
      >
        {isCreating ? 'Criar Funil' : 'Salvar Alterações'}
      </Button>
    </>
  ) : undefined;

  return (
    /* F76: header padrão do Drawer (título + subtítulo + ícone) — customHeader aposentado.
       A prop `tabs` do Drawer NÃO se aplica aqui: as abas pertencem ao painel de
       detalhe (master-detail), não ao header full-width do drawer. */
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      icon={<Icon name="filter" className="w-5 h-5" />}
      title="Configuração de Funis"
      subtitle={subtitle}
      {...(footer ? { footer } : {})}
    >
      <div className="flex h-full min-h-0" data-testid="funnels-drawer">
        {/* Coluna esquerda — lista de funis (master). No mobile as colunas
            empilham (lista OU detalhe, nunca as duas espremidas): com detalhe
            ativo a lista some e o painel ganha um "voltar". */}
        <div
          className={`${showDetail ? 'hidden lg:flex' : 'flex'} w-full lg:w-[280px] flex-shrink-0 flex-col lg:border-r min-h-0`}
          style={{ borderColor: '#e5e7eb', backgroundColor: '#fcfcfd' }}
        >
          <div className="flex-shrink-0 p-3 pb-0">
            {/* F01: primário manual → Button shared */}
            <Button
              variant="primary"
              icon="plus"
              fullWidth
              onClick={handleStartCreate}
              data-testid="funnels-drawer-new"
            >
              Novo funil
            </Button>
          </div>

          <div className="flex-shrink-0 p-3 pb-2">
            {/* F17-22: busca canônica (38px, ícone 16, X de limpar) */}
            <ToolbarSearch
              value={search}
              onChange={setSearch}
              placeholder="Buscar funil…"
              data-testid="funnels-drawer-search"
            />
          </div>

          <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-0">
            {isCreating && (
              <div
                className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg mb-1"
                style={{ border: '1px dashed #ddd6fe', backgroundColor: '#faf9ff' }}
                data-testid="funnels-drawer-draft-item"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: funnelForm.color }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate" style={{ color: '#7c3aed' }}>
                    {funnelForm.name || 'Novo funil'}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>
                    Rascunho
                  </div>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="text-center py-8 text-sm" style={{ color: '#64748b' }}>
                Carregando funis...
              </div>
            ) : filteredFunnels.length === 0 ? (
              !isCreating && (
                <div className="text-center py-8 text-sm px-2" style={{ color: '#94a3b8' }}>
                  Nenhum funil encontrado.
                </div>
              )
            ) : (
              filteredFunnels.map((funnel) => (
                <FunnelListItem
                  key={funnel.id}
                  funnel={funnel}
                  isSelected={!isCreating && selectedFunnelId === funnel.id}
                  onSelect={() => handleSelectFunnel(funnel)}
                  onDelete={() => void handleDeleteFunnel(funnel)}
                />
              ))
            )}
          </div>
        </div>

        {/* Coluna direita — detalhe com abas */}
        <div className={`${showDetail ? 'flex' : 'hidden lg:flex'} flex-1 flex-col min-w-0 min-h-0`}>
          {!showDetail ? (
            <div className="flex-1 flex items-center justify-center text-sm text-center px-6" style={{ color: '#94a3b8' }}>
              Selecione um funil à esquerda
            </div>
          ) : (
            /* Abas do painel de detalhe no primitivo DrawerTabBar (`.tabs2` do DS:
               largura do conteúdo, 14 semibold, recuo 18px). A prop `tabs` do Drawer
               NÃO serve aqui — master-detail: as abas são do painel direito, não do
               header full-width. Testids preservados via `items[].testid`. */
            <>
              {/* Mobile: volta para a lista de funis (colunas empilhadas) */}
              <button
                type="button"
                onClick={handleBackToList}
                className="lg:hidden flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b flex-shrink-0"
                style={{ color: '#7c3aed', borderColor: '#e5e7eb' }}
                data-testid="funnels-drawer-back"
              >
                <Icon name="chevron-left" className="w-4 h-4" />
                Funis
              </button>
              <DrawerTabBar
                items={tabs.map((tab) => ({
                  id: tab,
                  label: tab === 'capture' && funnelForm.capture !== null
                    ? (<span className="inline-flex items-center gap-1.5">Captura<span className="w-[7px] h-[7px] rounded-full" style={{ backgroundColor: '#10b981' }} /></span>)
                    : TAB_LABELS[tab],
                  testid: `funnels-drawer-tab-${tab}`,
                }))}
                active={activeTab}
                onChange={(id) => setActiveTab(id as FunnelDrawerTab)}
                data-testid="funnels-drawer-tabs"
              />
              <div className="flex-1 overflow-y-auto">
                <div className="p-6">
                {activeTab === 'basic' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                        Nome do Funil
                      </label>
                      <Input
                        type="text"
                        value={funnelForm.name}
                        onChange={(e) => setFunnelForm({ ...funnelForm, name: e.target.value })}
                        placeholder="Ex: Vendas, Atendimento, Suporte"
                        data-testid="funnels-drawer-field-name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                        Descrição (Opcional)
                      </label>
                      <Textarea
                        value={funnelForm.description}
                        onChange={(e) => setFunnelForm({ ...funnelForm, description: e.target.value })}
                        rows={3}
                        resize="none"
                        placeholder="Descreva o propósito deste funil"
                        data-testid="funnels-drawer-field-description"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                        Cor do Funil
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={funnelForm.color}
                          onChange={(e) => setFunnelForm({ ...funnelForm, color: e.target.value })}
                          className="w-12 h-12 rounded cursor-pointer border"
                          style={{ borderColor: '#d1d5db' }}
                          data-testid="funnels-drawer-field-color-picker"
                        />
                        <div className="flex-1">
                          <Input
                            type="text"
                            value={funnelForm.color}
                            onChange={(e) => setFunnelForm({ ...funnelForm, color: e.target.value })}
                            className="font-mono"
                            placeholder="#8b5cf6"
                            data-testid="funnels-drawer-field-color-hex"
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      className="flex items-center justify-between p-3 rounded-lg border"
                      style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
                    >
                      <div className="flex-1">
                        <label className="block text-sm font-medium" style={{ color: '#1e293b' }}>
                          Permitir leads duplicados
                        </label>
                        <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
                          Quando desativado, se um contato já possuir um lead em andamento, a descrição
                          será anexada ao lead existente ao invés de criar um novo. Leads ganhos ou
                          perdidos não bloqueiam a criação.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setFunnelForm({ ...funnelForm, allowDuplicateContacts: !funnelForm.allowDuplicateContacts })
                        }
                        className="ml-3 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out"
                        style={{ backgroundColor: funnelForm.allowDuplicateContacts ? '#8b5cf6' : '#d1d5db' }}
                        data-testid="funnels-drawer-toggle-duplicates"
                      >
                        <span
                          className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out"
                          style={{
                            transform: funnelForm.allowDuplicateContacts ? 'translateX(1.25rem)' : 'translateX(0)',
                          }}
                        />
                      </button>
                    </div>

                    {!funnelForm.allowDuplicateContacts && (
                      <div
                        className="p-3 rounded-lg border"
                        style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
                      >
                        <Select
                          label="Considerar leads"
                          description="Define onde procurar o lead em andamento do contato ao bloquear duplicados."
                          value={funnelForm.duplicateCheckScope}
                          onChange={(value) =>
                            setFunnelForm({
                              ...funnelForm,
                              duplicateCheckScope: value as 'funnel' | 'all_funnels',
                            })
                          }
                          options={[
                            { value: 'funnel', label: 'Deste funil' },
                            { value: 'all_funnels', label: 'De todos os funis' },
                          ]}
                          data-testid="funnels-drawer-duplicate-scope"
                        />
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'steps' && (
                  selectedFunnel ? (
                    <StepsManager funnelId={selectedFunnel.id} />
                  ) : (
                    <div className="text-center py-8 text-sm" style={{ color: '#94a3b8' }}>
                      Salve o funil primeiro para gerenciar as etapas.
                    </div>
                  )
                )}

                {activeTab === 'alerts' && selectedFunnel && (
                  <FunnelAlertsTab key={selectedFunnel.id} funnel={selectedFunnel} />
                )}

                {activeTab === 'types' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
                        Tipos de Lead
                      </label>
                      <p className="text-xs mb-3" style={{ color: '#64748b' }}>
                        Defina os tipos disponíveis para classificar leads neste funil. Esses tipos aparecerão
                        como opções no campo "Tipo" ao criar ou editar um lead.
                      </p>

                      <div className="flex gap-2 mb-3">
                        <div className="flex-1">
                          <Input
                            type="text"
                            value={newTypeName}
                            onChange={(e) => setNewTypeName(e.target.value)}
                            placeholder="Ex: Venda, Suporte..."
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddType();
                              }
                            }}
                            data-testid="funnels-drawer-field-new-type"
                          />
                        </div>
                        <Button
                          onClick={handleAddType}
                          variant="primary"
                          disabled={!newTypeName.trim() || funnelForm.types.includes(newTypeName.trim())}
                          data-testid="funnels-drawer-add-type"
                        >
                          <Icon name="plus" size={16} />
                        </Button>
                      </div>

                      {funnelForm.types.length === 0 ? (
                        <div
                          className="text-center py-6 text-sm rounded-lg border border-dashed"
                          style={{ color: '#9ca3af', borderColor: '#e5e7eb' }}
                        >
                          Nenhum tipo cadastrado. Adicione tipos para classificar seus leads.
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {funnelForm.types.map((typeName) => (
                            <div
                              key={typeName}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm"
                              style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}
                              data-testid={`funnels-drawer-type-${typeName}`}
                            >
                              <span>{typeName}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  setFunnelForm({
                                    ...funnelForm,
                                    types: funnelForm.types.filter((t) => t !== typeName),
                                  })
                                }
                                className="p-0.5 rounded-full transition-colors"
                                style={{ color: '#7c3aed' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(124, 58, 237, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                data-testid={`funnels-drawer-type-remove-${typeName}`}
                              >
                                <Icon name="x" size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'distribution' && (
                  <AssignmentConfigSection
                    config={funnelForm.assignmentConfig ?? { enabled: false, type: 'sequential' }}
                    onChange={(config) => setFunnelForm({ ...funnelForm, assignmentConfig: config })}
                    resourceType="funnel"
                    {...(selectedFunnel?.id ? { resourceId: selectedFunnel.id } : {})}
                    variant="inline"
                  />
                )}

                {activeTab === 'users' && selectedFunnel && (
                  <ContextUsersTab contextRef={{ kind: 'funnel', id: selectedFunnel.id }} />
                )}

                {activeTab === 'routing' && selectedFunnel && (
                  <RoutingRulesTab funnelId={selectedFunnel.id} />
                )}

                {activeTab === 'capture' && (
                  <FunnelCaptureTab
                    capture={funnelForm.capture}
                    onChange={(capture) =>
                      setFunnelForm({
                        ...funnelForm,
                        capture,
                        // D5 (decisão do coordenador): captura por QR exige contatos
                        // duplicados desligados — o backend rejeita 422 se não. Força
                        // só na transição desligada→ligada (evita reescrever o campo
                        // a cada edição de um campo já com a captura ligada).
                        ...(funnelForm.capture === null && capture !== null
                          ? { allowDuplicateContacts: false }
                          : {}),
                      })
                    }
                    enabled={isOpen}
                  />
                )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* RD2: destino dos leads antes de excluir um funil que não está vazio.
          O Modal usa portal pro body, então não é engolido pelo painel do Drawer. */}
      <FunnelDeleteModal
        isOpen={deleteTarget !== null}
        funnel={deleteTarget?.funnel ?? null}
        leadCount={deleteTarget?.leadCount ?? 0}
        onClose={() => setDeleteTarget(null)}
        onConfirm={(leads) => void handleConfirmDeleteWithLeads(leads)}
        isDeleting={deleteFunnel.isPending}
      />
    </Drawer>
  );
};

FunnelsSettingsDrawer.displayName = 'FunnelsSettingsDrawer';

// ============================================================================
// FunnelListItem — item da lista master (coluna esquerda)
// ============================================================================

interface FunnelListItemProps {
  funnel: FunnelResponse;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

const FunnelListItem: React.FC<FunnelListItemProps> = ({ funnel, isSelected, onSelect, onDelete }) => {
  const { data: stepsData } = useStepsByFunnelId(funnel.id);
  const stepsCount = stepsData?.length ?? 0;

  return (
    <div
      onClick={onSelect}
      className="group flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg cursor-pointer mb-0.5 transition-colors"
      style={{ backgroundColor: isSelected ? '#f5f3ff' : 'transparent' }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = '#f4f4f7';
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
      }}
      data-testid={`funnels-drawer-item-${funnel.id}`}
    >
      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: funnel.color }} />
      <div className="min-w-0 flex-1">
        <div
          className="text-sm font-semibold truncate"
          style={{ color: isSelected ? '#7c3aed' : '#334155' }}
        >
          {funnel.name}
        </div>
        <div className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>
          {stepsCount} {stepsCount === 1 ? 'etapa' : 'etapas'}
        </div>
      </div>
      {funnel.capture && (
        <span
          title="Captura ativa"
          className="w-[7px] h-[7px] rounded-full flex-shrink-0"
          style={{ backgroundColor: '#10b981' }}
          data-testid={`funnels-drawer-item-capture-${funnel.id}`}
        />
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded transition-opacity flex-shrink-0"
        style={{ color: '#cbd5e1' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#fef2f2';
          e.currentTarget.style.color = '#dc2626';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#cbd5e1';
        }}
        title="Excluir funil"
        data-testid={`funnels-drawer-delete-${funnel.id}`}
      >
        <Icon name="trash" className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
